import React from 'react'
import { motion } from 'framer-motion'
import { Users, Target, Award, Heart, CheckCircle, Eye, Rocket } from 'lucide-react'

const About = () => {
  React.useEffect(() => {
    document.title = "About Us - Shrijaa Outsourcing Consultants | Reliable HR & Financial Partner";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute("content", "Learn about Shrijaa Outsourcing Consultants, our ethical values, management practices, and how we deliver comprehensive recruitment solutions and premium financial support.");
    }

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', window.location.origin + '/about');
  }, []);

  const values = [
    {
      icon: <Users className="w-8 h-8" />,
      title: 'People-First Approach',
      description: 'We prioritize the human element in every interaction, ensuring both clients and candidates feel valued and supported.'
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: 'Precision Matching',
      description: 'Our thorough screening process ensures the perfect fit between talent and opportunity, every time.'
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: 'Excellence in Service',
      description: 'We maintain the highest standards in recruitment, delivering exceptional results that exceed expectations.'
    },
    {
      icon: <Heart className="w-8 h-8" />,
      title: 'Community Impact',
      description: 'We believe in building stronger communities by connecting people with meaningful career opportunities.'
    }
  ]

  const missionPoints = [
    'Provide skilled manpower tailored to client requirements.',
    'Ensure timely recruitment and deployment of personnel.',
    'Maintain compliance with labor laws and statutory regulations.',
    'Build long-term partnerships with clients through reliable service.'
  ]

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-800 via-primary-700 to-teal-700 text-white py-16 md:py-20">
        <div className="container-max">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">About Shrijaa Outsourcing Consultants</h1>
            <p className="text-lg md:text-xl text-primary-100">
              Your trusted partner in professional manpower solutions — connecting organizations with the right workforce efficiently and reliably.
            </p>
            <div className="w-20 h-1 bg-accent-500 mx-auto mt-6 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* About Us */}
      <section className="section-padding">
        <div className="container-max">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-primary-800 mb-6">
                Who We Are
              </h2>
              <div className="space-y-4 text-secondary-600 leading-relaxed">
                <p>
                  Shrijaa Outsourcing Consultants is a professional manpower solutions provider dedicated to helping organizations meet their workforce requirements efficiently and reliably.
                </p>
                <p>
                  We specialize in providing skilled, semi-skilled, and unskilled manpower across multiple industries through flexible employment models including permanent recruitment, temporary staffing, and contract manpower outsourcing.
                </p>
                <p>
                  Our goal is to simplify hiring and workforce management so that organizations can focus on their core business operations.
                </p>
              </div>
            </div>
            <div>
              <img
                src="/about-story.jpg"
                alt="Shrijaa Outsourcing team at work"
                className="rounded-lg shadow-xl"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="section-padding bg-secondary-50">
        <div className="container-max">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Vision */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-white rounded-xl shadow-lg p-8"
            >
              <div className="text-primary-600 mb-4 flex items-center gap-3">
                <Eye className="w-10 h-10" />
                <h2 className="text-3xl font-bold text-secondary-900">Our Vision</h2>
              </div>
              <p className="text-secondary-600 leading-relaxed text-lg">
                To become a trusted and preferred manpower outsourcing partner for organizations across India by delivering reliable workforce solutions with professionalism, integrity, and excellence.
              </p>
            </motion.div>

            {/* Mission */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-white rounded-xl shadow-lg p-8"
            >
              <div className="text-primary-600 mb-4 flex items-center gap-3">
                <Rocket className="w-10 h-10" />
                <h2 className="text-3xl font-bold text-secondary-900">Our Mission</h2>
              </div>
              <ul className="space-y-4">
                {missionPoints.map((point, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary-600 mt-1 flex-shrink-0" />
                    <span className="text-secondary-600 leading-relaxed text-lg">{point}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="section-padding">
        <div className="container-max">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
              Our Values
            </h2>
            <p className="text-xl text-secondary-600 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-primary-600 mb-4 flex justify-center">
                  {value.icon}
                </div>
                <h3 className="text-xl font-semibold text-primary-800 mb-3">
                  {value.title}
                </h3>
                <p className="text-secondary-600">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="section-padding bg-primary-600">
        <div className="container-max">
          <div className="text-center text-white mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              By the Numbers
            </h2>
            <p className="text-xl text-primary-100">
              Our impact speaks for itself
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="text-4xl md:text-5xl font-bold mb-2">2024</div>
              <div className="text-primary-100">Year Established</div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <div className="text-4xl md:text-5xl font-bold mb-2">300+</div>
              <div className="text-primary-100">Successful Placements</div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="text-4xl md:text-5xl font-bold mb-2">35+</div>
              <div className="text-primary-100">Happy Clients</div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <div className="text-4xl md:text-5xl font-bold mb-2">98%</div>
              <div className="text-primary-100">Client Satisfaction</div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default About
